import type {
  CanvasPoint,
  NodeOverlay,
} from 'features/knowledgeDocumentViewer/graph/canvasTypes'
import type {
  GraphScope,
  GraphTransport,
} from 'features/knowledgeDocumentViewer/graph/loadPageGraph'
import { pnidDatapointEdgeSchema } from 'features/knowledgeDocumentViewer/graph/pnidSchemas'
import type { LiveProcessValue } from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'
import type { MatchedDatapoint } from 'features/timeSeries'
import { z } from 'zod'

export type InstrumentAssociation = {
  sourceNodeId: string
  datapointId: string
}

export type CanvasRect = {
  denormalized_x: number
  denormalized_y: number
  denormalized_width: number
  denormalized_height: number
}

export function canvasRectFromBox(box: {
  x: number
  y: number
  width: number
  height: number
}): CanvasRect {
  return {
    denormalized_x: box.x,
    denormalized_y: box.y,
    denormalized_width: box.width,
    denormalized_height: box.height,
  }
}

export type GraphLiveAppearance = {
  overlay: NodeOverlay
  liveValue?: LiveProcessValue
  valueRect?: CanvasRect
}

const associationResponseSchema = z.array(z.unknown())
const associationWrapperSchema = z.object({
  entry: pnidDatapointEdgeSchema,
})
const datapointResponseSchema = z.object({
  entry: z.object({
    id: z.string(),
    type: z.literal('datapoints'),
    tags: z.object({
      site: z.string(),
      organization: z.string().optional(),
    }),
    data: z.object({
      pi_tag: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      point_source: z.string().nullable().optional(),
    }),
  }),
})

export function parseAssociatedDatapoint(
  value: unknown,
  scope: GraphScope
): MatchedDatapoint | undefined {
  const result = datapointResponseSchema.safeParse(value)
  if (!result.success) return undefined
  const { id, tags, data } = result.data.entry
  if (
    tags.site !== scope.site ||
    (tags.organization && tags.organization !== scope.organization)
  )
    return undefined
  const label = data.pi_tag?.trim() || data.name?.trim() || id
  return {
    id,
    pi_tag: label,
    name: data.name?.trim() || label,
    description: data.description ?? '',
    point_source: data.point_source || undefined,
  }
}

export function deduplicateAssociations(
  associations: InstrumentAssociation[]
): InstrumentAssociation[] {
  return [
    ...new Map(
      associations.map((association) => [
        JSON.stringify([association.sourceNodeId, association.datapointId]),
        association,
      ])
    ).values(),
  ]
}

export async function loadInstrumentAssociations(
  sourceIds: string[],
  scope: GraphScope,
  load: GraphTransport
): Promise<{ associations: InstrumentAssociation[]; failed: boolean }> {
  if (!sourceIds.length) return { associations: [], failed: false }
  const sourceSet = new Set(sourceIds)
  const associations: InstrumentAssociation[] = []
  let failed = false
  try {
    const rows = associationResponseSchema.parse(
      await load({
        type: ['pnid-edge'],
        tags: {
          organization: scope.organization,
          site: scope.site,
          sandbox: 'sandbox',
        },
        filters: [
          {
            field: 'data',
            key: 'type',
            op: '=',
            value: 'datapoint_association',
          },
          { field: 'data', key: 'source_node_id', op: 'in', value: sourceIds },
        ],
        limit: null,
      })
    )
    for (const row of rows) {
      const parsed = associationWrapperSchema.safeParse(row)
      if (!parsed.success) {
        failed = true
        continue
      }
      const edge = parsed.data.entry
      if (
        edge.tags.organization !== scope.organization ||
        edge.tags.site !== scope.site ||
        !sourceSet.has(edge.data.source_node_id) ||
        (edge.tags['pipeline-config'] !== null &&
          edge.tags['pipeline-config'] !== undefined &&
          edge.tags['pipeline-config'] !== scope['pipeline-config'])
      )
        continue
      associations.push({
        sourceNodeId: edge.data.source_node_id,
        datapointId: edge.data.target_node_id,
      })
    }
  } catch {
    failed = true
  }
  return { associations: deduplicateAssociations(associations), failed }
}

export function nodeOverlayBounds(overlay: NodeOverlay): CanvasRect {
  const xs = overlay.geometry.points.map((point) => point.denormalized_x)
  const ys = overlay.geometry.points.map((point) => point.denormalized_y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return {
    denormalized_x: x,
    denormalized_y: y,
    denormalized_width: Math.max(...xs) - x,
    denormalized_height: Math.max(...ys) - y,
  }
}

export function rectsOverlap(a: CanvasRect, b: CanvasRect): boolean {
  return (
    a.denormalized_x < b.denormalized_x + b.denormalized_width &&
    b.denormalized_x < a.denormalized_x + a.denormalized_width &&
    a.denormalized_y < b.denormalized_y + b.denormalized_height &&
    b.denormalized_y < a.denormalized_y + a.denormalized_height
  )
}

export function liveValueRectForOverlay(overlay: NodeOverlay): CanvasRect {
  const bounds = nodeOverlayBounds(overlay)
  const gap = bounds.denormalized_width * 0.05
  return {
    denormalized_x: bounds.denormalized_x,
    denormalized_y: bounds.denormalized_y + bounds.denormalized_height + gap,
    denormalized_width: bounds.denormalized_width,
    denormalized_height: bounds.denormalized_width * 0.5,
  }
}

export function hitTestLiveValueAppearances(
  appearances: GraphLiveAppearance[],
  point: CanvasPoint
): NodeOverlay | undefined {
  return appearances.find(
    ({ valueRect: rect }) =>
      rect &&
      point.denormalized_x >= rect.denormalized_x &&
      point.denormalized_x <= rect.denormalized_x + rect.denormalized_width &&
      point.denormalized_y >= rect.denormalized_y &&
      point.denormalized_y <= rect.denormalized_y + rect.denormalized_height
  )?.overlay
}
